// src/utils/offlineQueue.js
// File d'attente offline pour actions non-financières (IndexedDB)

import { openDB } from 'idb';

const DB_NAME = 'INSULATED-offline-queue';
const DB_VERSION = 1;
const STORE_NAME = 'pending-actions';

const MAX_QUEUE_SIZE = 50;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

// ============================================================
// Whitelist : routes éligibles à la file d'attente
// ============================================================
const QUEUEABLE_ROUTES = [
    { method: 'POST', pattern: /\/tickets\/[^/]+\/messages/ },
    { method: 'POST', pattern: /\/tickets$/ },
    { method: 'PUT',  pattern: /\/notifications\/[^/]+\/read/ },
    { method: 'PUT',  pattern: /\/user\/preferences/ },
];

// ============================================================
// Blacklist : routes JAMAIS mises en queue (sécurité)
// ============================================================
const NEVER_QUEUE = [
    /\/investment\//,
    /\/transactions\//,
    /\/withdrawals/,
    /\/transfers/,
    /\/deposits/,
    /\/auth\//,
    /\/user\/password/,
    /\/user\/profile-picture/,
    /\/logs\//,
];

// ============================================================
// Utilitaires internes
// ============================================================
// eslint-disable-next-line no-unused-vars
const isOnline = () =>
    typeof navigator !== 'undefined' ? navigator.onLine : true;

const getDB = async () => {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
                store.createIndex('timestamp', 'timestamp');
                store.createIndex('status', 'status');
            }
        },
    });
};

// ============================================================
// API publique
// ============================================================

/**
 * Détermine si une requête peut être mise en queue.
 */
export const isQueueable = (method, url) => {
    const m = (method || 'GET').toUpperCase();
    const u = url || '';

    if (NEVER_QUEUE.some((p) => p.test(u))) return false;
    return QUEUEABLE_ROUTES.some(
        (r) => r.method === m && r.pattern.test(u)
    );
};

/**
 * Ajoute une action à la file d'attente.
 * Retourne l'id de l'action ou null si rejetée.
 */
export const enqueue = async ({
    url,
    method,
    body,
    headers = {},
    metadata = {},
}) => {
    if (!isQueueable(method, url)) {
        console.warn('[offlineQueue] Action non-queueable :', method, url);
        return null;
    }

    const db = await getDB();

    // Nettoyage des actions trop anciennes
    await cleanupOldActions();

    // Limite de taille
    const count = await db.count(STORE_NAME);
    if (count >= MAX_QUEUE_SIZE) {
        console.warn('[offlineQueue] File pleine, drop de l\'action');
        return null;
    }

    const action = {
        url,
        method,
        body,
        headers,
        metadata,
        timestamp: Date.now(),
        status: 'pending',
        retries: 0,
        lastError: null,
        idempotencyKey: generateIdempotencyKey(),
    };

    const id = await db.add(STORE_NAME, action);

    // Notifier l'UI
    window.dispatchEvent(
        new CustomEvent('offline-queue:updated', { detail: { count: count + 1 } })
    );

    return id;
};

/**
 * Retourne toutes les actions en attente (triées par timestamp).
 */
export const getAllPending = async () => {
    const db = await getDB();
    const all = await db.getAllFromIndex(STORE_NAME, 'timestamp');
    return all.filter((a) => a.status === 'pending');
};

/**
 * Nombre d'actions en attente.
 */
export const getPendingCount = async () => {
    const db = await getDB();
    const all = await db.getAll(STORE_NAME);
    return all.filter((a) => a.status === 'pending').length;
};

/**
 * Supprime une action terminée.
 */
export const removeAction = async (id) => {
    const db = await getDB();
    await db.delete(STORE_NAME, id);
    await notifyUpdate();
};

/**
 * Marque une action comme échouée définitivement.
 */
export const markFailed = async (id, error) => {
    const db = await getDB();
    const action = await db.get(STORE_NAME, id);
    if (!action) return;

    action.status = 'failed';
    action.lastError = String(error);
    await db.put(STORE_NAME, action);
};

/**
 * Incrémente le compteur de retry.
 */
export const incrementRetry = async (id) => {
    const db = await getDB();
    const action = await db.get(STORE_NAME, id);
    if (!action) return;

    action.retries = (action.retries || 0) + 1;
    await db.put(STORE_NAME, action);
    return action.retries;
};

/**
 * Vide toute la file (utile en debug / logout).
 */
export const clearQueue = async () => {
    const db = await getDB();
    await db.clear(STORE_NAME);
    await notifyUpdate();
};

/**
 * Supprime les actions > 24h.
 */
export const cleanupOldActions = async () => {
    const db = await getDB();
    const all = await db.getAll(STORE_NAME);
    const now = Date.now();

    for (const action of all) {
        if (now - action.timestamp > MAX_AGE_MS) {
            await db.delete(STORE_NAME, action.id);
        }
    }
};

/**
 * Génère une clé d'idempotence unique (pour éviter les doublons côté backend).
 */
const generateIdempotencyKey = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Notifie l'UI du changement.
 */
const notifyUpdate = async () => {
    const count = await getPendingCount();
    window.dispatchEvent(
        new CustomEvent('offline-queue:updated', { detail: { count } })
    );
};